import isEmpty from "lodash/isEmpty";

import store from "../../store";
import {dbFeatureFilterProperties, dbFeatureRelatedFeaturesProperties, otherMissingProperties} from "./constants";
import {hasSubscriptionForRoom} from "../../misc/visualizerSwitchingFunctions";
import {getRoomNameToCompare} from "../../misc/categorySwitchingFunctions";
import {isNewNavEnabled} from "../../misc/newNavFunctions";
import {shareAr3dUrlStrings} from "../../misc/dbFeatureSlim";
import {getLoadedDbFeature} from "../../misc/featureStore";


/**
 * Checks whether to show room feature as in visualizer in the navigation
 * @param feature {featureInterface}
 * @returns {featureInterface}
 */
export const setRoomNotInVisualizerProperty = (feature) => {
  const fabricatorSubscriptions = store.getState().fabricatorState.fabricatorSubscriptions;
  if (!feature || !feature.dbFeature || !feature.dbFeature.sharedFeatureName) {
    return feature
  }

  // const visType = getCommonVisualizerType();
  // if (name === visType) {
  //   feature.notAvailableInVisualizer = false;
  //   feature.dbFeature.unlocked = true;
  //   return feature
  // }
  if (!Array.isArray(fabricatorSubscriptions)) {
    return feature
  }
  const name = feature.dbFeature.sharedFeatureName.toLowerCase();
  const subscription = fabricatorSubscriptions.find(subscr => subscr.service.toLowerCase() === name)
    || hasSubscriptionForRoom(getRoomNameToCompare(feature));
  if (subscription) {
    feature.notAvailableInVisualizer = false;
    feature.dbFeature.unlocked = true;
    if (subscription.thumbnail) {
      feature.customThumbnail = subscription.thumbnail
    }
  }
  return feature
}

/**
 * updates existing dbFeature
 * @param dbFeature {dbFeatureInterface} // new
 * @param existingDbFeature {dbFeatureInterface} // old (existing)
 * @param layout {object} // layout
 */
export const updateExistingFeature = (dbFeature, existingDbFeature, layout) => {
  if (!existingDbFeature["relatedFeatureIds"] && Array.isArray(dbFeature["relatedFeatureIds"])) {
    existingDbFeature["relatedFeatureIds"] = dbFeature["relatedFeatureIds"];
  }
  // loadRelatedFeatures(dbFeature, layout);
  dbFeatureRelatedFeaturesProperties.forEach(property => {
    // always update related features, because they can be changed in the features response(updated markup issue)
    if (Array.isArray(dbFeature[property])
      // unless it's empty array, then we don't need to update it, because it's already loaded
      && !(property === "relatedFeatures" && existingDbFeature[property] && existingDbFeature[property].length && !dbFeature[property].length)) {
      existingDbFeature[property] = dbFeature[property];
      const parentGroups = layout.getFeatures(true);
      dbFeature[property].forEach(dbFeature => {
        layout.loadDbFeature(dbFeature, parentGroups)
      })
    }
  })
  if (layout.featureStore) {
    layout.featureStore.canonicalizeRelated(existingDbFeature); // response copies -> canonical (slimmed, current prices) objects
  }
  if ((!Array.isArray(existingDbFeature.stonePricingId) || !existingDbFeature.stonePricingId.length || existingDbFeature.stonePricingId[0] === -1)
    && (Array.isArray(dbFeature.stonePricingId) && dbFeature.stonePricingId.length && dbFeature.stonePricingId[0] !== -1)) {
    existingDbFeature.stonePricingId = dbFeature.stonePricingId
  }
  // the custom (manually changed or restored from the job) price always wins over the response price.
  // NOTE: we have to check the flag on the EXISTING (live) feature, the one that comes in the response never has it,
  // unless it was restored from the job by restoreSavedCustomFeaturePrices
  if (existingDbFeature.priceWasChanged) {
    // do nothing, we can't overwrite a custom price from the response
  } else if (dbFeature.priceWasChanged) { // the job price was restored onto the new dbFeature - move it to the existing one
    existingDbFeature.priceWasChanged = true;
    existingDbFeature.cost = dbFeature.cost;
    existingDbFeature.fabricatorCost = dbFeature.fabricatorCost;
    existingDbFeature.price = dbFeature.price;
  } else {
    existingDbFeature.cost = dbFeature.cost; // not sure about this one, but let it be, as it seems like the main response is not always correct
    existingDbFeature.price = dbFeature.price; // price sometimes comes as null in the main response, so we need to update it
  }
  dbFeatureFilterProperties.forEach((key) => {
    if ((!existingDbFeature[key] || isEmpty(existingDbFeature[key])) && dbFeature[key] && !isEmpty(dbFeature[key])) {
      existingDbFeature[key] = dbFeature[key];
      dbFeature.wasUpdatedFromAnotherResponse = true; // to erase ._customDisplayName, because we need filters to create it again!
      existingDbFeature.wasUpdatedFromAnotherResponse = true; // to erase ._customDisplayName, because we need filters to create it again!
    }
  });
  let otherPropertiesCopied = false;
  otherMissingProperties.forEach((key) => {
    if ((!existingDbFeature[key] || isEmpty(existingDbFeature[key])) && dbFeature[key] && !isEmpty(dbFeature[key])) {
      existingDbFeature[key] = dbFeature[key];
      otherPropertiesCopied = true;
    }
  });
  if (otherPropertiesCopied) {
    shareAr3dUrlStrings(existingDbFeature); // AR urls could come from this response, share their strings with the metadata keys
  }
  if (dbFeature.markUpString) {
    existingDbFeature.markUpString = dbFeature.markUpString; // just to be able to compare it later
    if (!existingDbFeature.priceWasChanged) { // the custom price already includes the mark up, we can't apply it again
      existingDbFeature.price = dbFeature.price;
      existingDbFeature.cost = dbFeature.cost;
      existingDbFeature.discount = dbFeature.discount;
      existingDbFeature.shippingCost = dbFeature.shippingCost;
    }
  }
}


export const loadRelatedFeatures = (dbFeature, layout) => {
  if (!Array.isArray(dbFeature.relatedFeatureIds) || !dbFeature.relatedFeatureIds.length) {
    return
  }
  // console.log("loadRelatedFeatures!!", dbFeature);
  dbFeature.relatedFeatureIds.forEach(relFeatureId => {
    if (!dbFeature.relatedFeatures) {
      dbFeature.relatedFeatures = [];
    }
    if (dbFeature.relatedFeatures.some(relFeature => relFeature.id === relFeatureId)) {
      return // already loaded
    }
    const loadedDbFeature = getLoadedDbFeature(layout, relFeatureId); // O(1) with layout.featureStore
    if (loadedDbFeature) {
      dbFeature.relatedFeatures.push(loadedDbFeature);
    } else {
      console.log("related feature wasn't loaded"); // it it fires - then something is wrong, we should load related before main features
    }
  })
  // console.log("dbFeature.relatedFeatures", dbFeature.relatedFeatures);
}

export const featureHasLayers = (feature) => {
  return !!(feature.dbFeature && feature.dbFeature.s3Bindings && feature.dbFeature.s3Bindings.length)
};

/**
 * `feature.notAvailableInVisualizer` is a STATIC property: it's set when the feature is loaded
 * (no s3Bindings / no RoomLayout) and may be unlocked later (room subscriptions, kv edge match).
 * Many places (filters, sink <-> vanity matching, displayLogic, layouts...) rely on that static
 * meaning, so it must NOT be rewritten per scenario.
 *
 * This helper answers the DYNAMIC question "can this feature render in the CURRENT scenario?" for
 * a single feature (clicked / selected / active). With UNav (isNewNavEnabled) a feature with layers can be
 * listed even when it doesn't match the current conditions (e.g. it only exists in another room
 * layout), so it's treated as not in visualizer too. Without UNav (isNewNavEnabled) it's the static flag.
 *
 * Only use it for ONE feature at a time (click handlers, the selected feature UI). For navigation
 * lists use isNavItemNotInVisualizer(navItem) - that value is already resolved by getUNavigationItems().
 *
 * @param feature {featureInterface}
 * @returns {boolean}
 */
/**
 * Visualizer type of the layout a feature group belongs to (falls back to the page visualizer type)
 * @param featureGroup {featureGroupInterface}
 * @returns {string}
 */
export const getFeatureGroupVisualizerType = (featureGroup) => {
  if (!featureGroup) {
    return document.kvConfig.visualizerType
  }
  const parent = typeof featureGroup.getParent === "function" ? featureGroup.getParent() : null;
  const layout = featureGroup.layout || (parent && parent.layout);
  return (layout && layout.visualizerType) || document.kvConfig.visualizerType
};

export const isFeatureNotInVisualizerNow = (feature) => {
  if (!feature) {
    return false
  }
  if (feature.notAvailableInVisualizer) {
    return true
  }
  if (!featureHasLayers(feature) || typeof feature.matchesMetaForArrayOfConditions !== "function"
    || typeof feature.getParent !== "function") {
    return false
  }
  const parent = feature.getParent();
  if (!parent || typeof parent.getArrayOfConditions !== "function" || !isNewNavEnabled(getFeatureGroupVisualizerType(parent))) {
    return false
  }
  const conditions = parent.getArrayOfConditions(true);
  return !feature.matchesMetaForArrayOfConditions(conditions); // same conditions as getUNavigationItems()
};

/**
 * "Not in visualizer" for a navigation item. UNavItems (UNav, isNewNavEnabled) carry the scenario resolved
 * value in `cantRenderInVisualizer` (set by getUNavigationItems()), other items fall back to the static
 * flag of their part.
 * @param navItem {DesignNavigationItem|UNavItem}
 * @returns {boolean}
 */
export const isNavItemNotInVisualizer = (navItem) => {
  if (!navItem) {
    return false
  }
  if (typeof navItem.cantRenderInVisualizer === "boolean") {
    return navItem.cantRenderInVisualizer
  }
  return !!(navItem.part && navItem.part.notAvailableInVisualizer)
};
